// src/lib/analytics.ts
import posthog from "posthog-js";
import { isPublicAnalyticsEnabled } from "@/lib/feature-flags";

let initialized = false;

const INITIAL_UTM_FLAG = "rc_initial_utm_set";
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export function initPostHog(): void {
  if (initialized) return;

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return;

  posthog.init(key, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
    person_profiles: "identified_only",
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: true,
    disable_session_recording: true,
    persistence: "localStorage",
    loaded: (ph) => {
      if (process.env.NODE_ENV === "development") ph.debug();
    },
  });

  initialized = true;
}

function capture(event: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.capture(event, properties);
}

// --- Eventos de registro ---

export function trackUserRegistered(email: string): void {
  capture("user_registered", { email });
}

export function trackEmailVerified(email: string): void {
  capture("email_verified", { email });
}

// --- Eventos de welcome ---

export function trackWelcomeViewed(): void {
  capture("welcome_viewed");
}

export function trackWelcomeCtaClicked(): void {
  capture("welcome_cta_clicked");
}

// --- Eventos del wizard ---

export function trackWizardStarted(): void {
  capture("wizard_started");
}

export function trackWizardStepCompleted(step: 1 | 2 | 3 | 4): void {
  capture(`wizard_step_${step}_completed`);
}

// --- Eventos de generación ---

export function trackPresetGenerationStarted(props: {
  modeler: string;
  song: string;
  artist: string;
}): void {
  capture("preset_generation_started", props);
}

export function trackPresetGenerationCompleted(props: {
  presetId: string;
}): void {
  capture("preset_generation_completed", props);
}

export function trackPresetGenerationFailed(props: {
  error: string;
}): void {
  capture("preset_generation_failed", props);
}

// --- Eventos de preset ---

export function trackPresetViewed(props: {
  presetId: string;
  modeler: string;
}): void {
  capture("preset_viewed", props);
}

export function trackPresetIterated(props: {
  presetId: string;
  version: number;
}): void {
  capture("preset_iterated", props);
}

export function trackPresetRated(props: {
  presetId: string;
  rating: number;
}): void {
  capture("preset_rated", props);
}

// --- Journey del primer preset (FEATURE-131) ---
// Funnel autenticado: el usuario ya está identificado por AuthContext,
// así que estos eventos se asocian al perfil sin pasar user_id.

export function trackJourneyShown(): void {
  capture("journey_shown");
}

export function trackJourneyStarted(): void {
  capture("journey_started");
}

export function trackJourneyStep(props: {
  step: 1 | 2 | 3;
  key: "chain" | "rate" | "improve";
}): void {
  capture("journey_step", props);
}

export function trackJourneyCompleted(): void {
  capture("journey_completed");
}

export function trackJourneyDismissed(props: { step: 1 | 2 | 3 }): void {
  capture("journey_dismissed", props);
}

// --- Centro de notificaciones (FEATURE-132) ---

export function trackWhatsNewOpened(props: { unseenCount: number }): void {
  capture("whats_new_opened", props);
}

export function trackWhatsNewCtaClicked(props: { entryId: string }): void {
  capture("whats_new_cta_clicked", props);
}

// --- Eventos públicos (landing) ---
// Gated por `isPublicAnalyticsEnabled` (NEXT_PUBLIC_ENABLE_PUBLIC_ANALYTICS).
// El flag desactiva solo el tracking público; el funnel autenticado
// (identify, wizard, generación, rating) sigue activo.

export function trackPageview(url: string): void {
  if (!isPublicAnalyticsEnabled) return;
  capture("$pageview", { $current_url: url });
}

export function trackCtaClicked(props: {
  location: "hero" | "pricing" | "footer";
  label: string;
}): void {
  if (!isPublicAnalyticsEnabled) return;
  capture("cta_clicked", props);
}

// --- Eventos de waitlist ---

export function trackWaitlistViewed(): void {
  if (!isPublicAnalyticsEnabled) return;
  capture("waitlist_viewed");
}

export function trackWaitlistSubmitted(): void {
  if (!isPublicAnalyticsEnabled) return;
  capture("waitlist_submitted");
}

// --- First-touch UTM attribution ---

// Persiste utm_* + referrer del primer pageview de la sesión como propiedades
// initial_* del perfil vía $set_once. Sobrevive a sucesivos pageviews y al
// identify() del registro porque PostHog mergea propiedades anónimas al perfil
// identificado.
export function captureInitialUtm(searchParams: URLSearchParams): void {
  if (!isPublicAnalyticsEnabled) return;
  if (!initialized) return;
  if (typeof window === "undefined") return;
  if (window.sessionStorage.getItem(INITIAL_UTM_FLAG)) return;

  const setOnceProps: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = searchParams.get(key);
    if (value) setOnceProps[`initial_${key}`] = value;
  }
  if (typeof document !== "undefined" && document.referrer) {
    setOnceProps.initial_referrer = document.referrer;
  }

  window.sessionStorage.setItem(INITIAL_UTM_FLAG, "1");

  if (Object.keys(setOnceProps).length > 0) {
    posthog.setPersonProperties(undefined, setOnceProps);
  }
}

// --- Identificación ---

export function identifyUser(
  userId: string,
  traits: { email: string; name: string }
): void {
  if (!initialized) return;
  posthog.identify(userId, traits);
}

export function resetUser(): void {
  if (!initialized) return;
  posthog.reset();
}
